import { Link } from 'react-router-dom';
import {
  buildCitScItemMetrics,
  citDepositStatus,
  citDocumentStatusClass,
  formatCitQty,
} from '../../lib/citList';
import type { ApplicationSubmission, CustomiseItemsTransferFormData } from '../../types/applicationForm';

type Props = {
  row: ApplicationSubmission;
  detailPath: string;
};

export function CitScListRow({ row, detailPath }: Props) {
  const data = row.form_data as CustomiseItemsTransferFormData;
  const scNo = data.sales_confirmation_no?.trim() || '—';
  const customerCode = data.customer_code?.trim() || '';
  const customerName = data.customer_name?.trim() || '';
  const metrics = buildCitScItemMetrics(data);
  const deposit = citDepositStatus(data);
  const docStatus = data.status?.trim() || '—';

  return (
    <Link to={detailPath} className="cit-sc-list-row">
      <span className="cit-sc-list-row__sc mono">{scNo}</span>

      <span className="cit-sc-list-row__customer">
        {customerCode ? (
          <span className="cit-sc-list-row__customer-code mono">{customerCode}</span>
        ) : null}
        <span className="cit-sc-list-row__customer-name">
          {customerName || (customerCode ? '' : '—')}
        </span>
      </span>

      <span className="cit-sc-list-row__metrics">
        <span className="cit-sc-list-row__metrics-head" aria-hidden>
          <span>Item</span>
          <span>SOC qty</span>
          <span>Paid qty</span>
          <span>Offset qty</span>
          <span>Balance qty</span>
        </span>
        {metrics.length === 0 ? (
          <span className="cit-sc-list-row__item-empty">No items</span>
        ) : (
          metrics.map(({ item, socQty, paidQty, offsetQty, balanceQty }) => {
            const offsetOverPaid = offsetQty > paidQty + 1e-9;
            return (
              <span key={item.id} className="cit-sc-list-row__metric-line">
                <span className="cit-sc-list-row__item-cell">
                  <span className="cit-sc-list-row__item-code mono">
                    {item.item_no.trim() || '—'}
                  </span>
                  {item.description.trim() ? (
                    <span className="cit-sc-list-row__item-desc">{item.description}</span>
                  ) : null}
                </span>
                <span className="cit-sc-list-row__qty">
                  {formatCitQty(socQty)}
                  {item.uom.trim() ? (
                    <span className="cit-sc-list-row__uom">{item.uom}</span>
                  ) : null}
                </span>
                <span className="cit-sc-list-row__qty">{formatCitQty(paidQty)}</span>
                <span
                  className={`cit-sc-list-row__qty${
                    offsetOverPaid ? ' cit-sc-list-row__qty--offset-over' : ''
                  }`}
                >
                  {formatCitQty(offsetQty)}
                </span>
                <span
                  className={`cit-sc-list-row__qty${
                    balanceQty < 0 ? ' cit-sc-list-row__qty--balance-neg' : ''
                  }`}
                >
                  {formatCitQty(balanceQty)}
                </span>
              </span>
            );
          })
        )}
      </span>

      <span className="cit-sc-list-row__deposit">
        {deposit.na ? (
          <span className="cit-deposit-pill cit-deposit-pill--na">N/A</span>
        ) : deposit.credited ? (
          <span className="cit-deposit-pill cit-deposit-pill--credited">Credited</span>
        ) : deposit.unpaidLabels.length > 0 ? (
          <>
            <span className="cit-deposit-pill cit-deposit-pill--unpaid">Unpaid</span>
            <span className="cit-sc-list-row__deposit-detail">
              {deposit.unpaidLabels.map((label) => `${label} unpaid`).join(' · ')}
            </span>
          </>
        ) : deposit.partial ? (
          <>
            <span className="cit-deposit-pill cit-deposit-pill--partial">Paid Partially</span>
            <span className="cit-sc-list-row__deposit-detail">
              Awaiting balance deposit...
            </span>
          </>
        ) : deposit.overpaid ? (
          <span className="cit-deposit-pill cit-deposit-pill--overpaid">Overpaid</span>
        ) : deposit.paid ? (
          <span className="cit-deposit-pill cit-deposit-pill--paid">Paid</span>
        ) : (
          <span className="cit-deposit-pill cit-deposit-pill--unpaid">Unpaid</span>
        )}
      </span>

      <span className={citDocumentStatusClass(docStatus)}>{docStatus}</span>

      <span className="cit-sc-list-row__chevron" aria-hidden>
        ›
      </span>
    </Link>
  );
}
